import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import { History as HistoryIcon, Restore as RestoreIcon } from '@mui/icons-material';
import { EasyCheckButton } from '../../common/Controls';
import { chequeTemplateService } from '../../../services/templates/chequeTemplateService';

const ACTION_LABELS = {
  UPDATE: { label: 'Before edit', color: 'primary' },
  DELETE: { label: 'Before delete', color: 'error' },
  RESTORE: { label: 'Before restore', color: 'warning' },
  IMPORT: { label: 'Before import', color: 'info' },
};

// Version history for a bank's cheque template (Bank Backup module).
// Restoring snapshots the current template first, so a restore is itself undoable.
const TemplateHistoryDialog = ({ open, onClose, bank, onRestored }) => {
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [confirmItem, setConfirmItem] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);

  const pick = (item, pascal, camel) => (item[pascal] !== undefined ? item[pascal] : item[camel]);

  const loadHistory = useCallback(async () => {
    if (!bank?.id) return;
    setLoading(true);
    setError(null);
    try {
      const items = await chequeTemplateService.getTemplateHistory(bank.id);
      setHistory(items);
    } catch (e) {
      setError(e.message || 'Failed to load template history');
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [bank?.id]);

  useEffect(() => {
    if (open) {
      setConfirmItem(null);
      loadHistory();
    }
  }, [open, loadHistory]);

  const handleRestore = async (item) => {
    const historyId = pick(item, 'Id', 'id');
    setRestoringId(historyId);
    setError(null);
    try {
      await chequeTemplateService.restoreTemplateVersion(historyId);
      setConfirmItem(null);
      onRestored?.();
      loadHistory();
    } catch (e) {
      setError(e.message || 'Failed to restore template version');
    } finally {
      setRestoringId(null);
    }
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return isNaN(date.getTime()) ? '—' : date.toLocaleString();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <HistoryIcon color="primary" />
          <Box>
            <Typography variant="h6">Template Version History</Typography>
            {bank && (
              <Typography variant="caption" color="text.secondary">
                {bank.accountName} ({bank.accountNumber})
              </Typography>
            )}
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : history.length === 0 ? (
          <Alert severity="info">
            No snapshots yet. A snapshot is saved automatically every time this bank's template is
            edited or deleted (while Bank Configuration Backup is enabled).
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              The last {history.length} snapshot{history.length === 1 ? '' : 's'} are kept (max 10).
              Restoring saves the current settings as a new snapshot first.
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Saved</TableCell>
                    <TableCell>Snapshot taken</TableCell>
                    <TableCell>By</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((item) => {
                    const historyId = pick(item, 'Id', 'id');
                    const action = pick(item, 'Action', 'action') || '';
                    const actionMeta = ACTION_LABELS[action] || { label: action, color: 'default' };
                    const isConfirming = confirmItem === historyId;
                    return (
                      <TableRow key={historyId} hover>
                        <TableCell>{formatDate(pick(item, 'SnapshotAt', 'snapshotAt'))}</TableCell>
                        <TableCell>
                          <Chip size="small" label={actionMeta.label} color={actionMeta.color} variant="outlined" />
                        </TableCell>
                        <TableCell>{pick(item, 'SnapshotBy', 'snapshotBy') || '—'}</TableCell>
                        <TableCell align="right">
                          {isConfirming ? (
                            <Box sx={{ display: 'inline-flex', gap: 1 }}>
                              <EasyCheckButton
                                size="small"
                                color="warning"
                                onClick={() => handleRestore(item)}
                                loading={restoringId === historyId}
                              >
                                Confirm
                              </EasyCheckButton>
                              <EasyCheckButton
                                size="small"
                                variant="outlined"
                                onClick={() => setConfirmItem(null)}
                                disabled={restoringId === historyId}
                              >
                                Cancel
                              </EasyCheckButton>
                            </Box>
                          ) : (
                            <EasyCheckButton
                              size="small"
                              variant="outlined"
                              startIcon={<RestoreIcon />}
                              onClick={() => setConfirmItem(historyId)}
                              disabled={restoringId !== null}
                            >
                              Restore
                            </EasyCheckButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <EasyCheckButton variant="outlined" onClick={onClose}>
          Close
        </EasyCheckButton>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateHistoryDialog;
